/**
 * Service de gestion des cryptomonnaies avec cache et APIs identiques à web-platform
 */

// Service de cache pour optimiser les appels API
class CacheService {
  private cache: { [key: string]: any } = {};
  private expirations: { [key: string]: number } = {};

  get(key: string): any | null {
    if (!this.cache.hasOwnProperty(key)) {
      return null;
    }

    const now = Date.now();
    if (this.expirations[key] && now > this.expirations[key]) {
      this.remove(key);
      return null;
    }

    return this.cache[key];
  }

  set(key: string, value: any, ttl: number = 5 * 60 * 1000): void {
    this.cache[key] = value;
    
    if (ttl > 0) {
      const now = Date.now();
      this.expirations[key] = now + ttl;
    }
  }

  remove(key: string): void {
    delete this.cache[key];
    delete this.expirations[key];
  }

  clear(): void {
    this.cache = {};
    this.expirations = {};
  }

  async getOrFetch<T>(key: string, fetchFunction: () => Promise<T>, ttl: number = 5 * 60 * 1000): Promise<T> {
    const cachedValue = this.get(key);
    if (cachedValue !== null) {
      return cachedValue;
    }

    try {
      const value = await fetchFunction();
      if (value !== null && value !== undefined) {
        this.set(key, value, ttl);
      }
      return value;
    } catch (error) {
      console.error(`Erreur lors de la récupération de la valeur pour la clé ${key}:`, error);
      throw error;
    }
  }
}

// Durées de cache prédéfinies
const CACHE_DURATIONS = {
  PRICE: 2 * 60 * 1000,         // 2 minutes pour les prix (volatiles)
  EXCHANGE_RATE: 60 * 60 * 1000, // 1 heure pour les taux de change (moins volatiles)
  ICONS: 24 * 60 * 60 * 1000,    // 24 heures pour les icônes (statiques)
  PORTFOLIO: 5 * 60 * 1000       // 5 minutes pour les données de portefeuille
};

// Instance unique du service de cache
const cacheService = new CacheService();

// Devise par défaut
const DEVISE = "EUR";

/**
 * Convertit un montant USD en EUR en utilisant le taux de change mis en cache
 */
async function convertUSDEUR(montantUSD: number): Promise<number> {
  const tauxSecours = 0.92; // valeur de secours
  
  try {
    const tauxConversion = await cacheService.getOrFetch(
      'usd-eur-rate',
      async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 50000);
        
        try {
          const response = await fetch('https://currency-cryptonvx.currency-api.workers.dev/v1/exchange?from=USD&to=EUR', {
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            console.warn('Erreur lors de la récupération du taux de conversion USD/EUR, utilisation du taux de secours');
            return tauxSecours;
          }
          
          const data = await response.json();
          return (data && data.rates && data.rates.EUR) ? data.rates.EUR : tauxSecours;
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      },
      CACHE_DURATIONS.EXCHANGE_RATE
    );
    
    return montantUSD * tauxConversion;
  } catch (error) {
    console.warn('Erreur lors de la conversion USD/EUR:', error);
    return montantUSD * tauxSecours;
  }
}

/**
 * Récupère le prix d'une cryptomonnaie avec mise en cache
 */
async function getPriceForSymbol(symbol: string): Promise<number | null> {
  if (!symbol) return null;
  
  try {
    const price = await cacheService.getOrFetch(
      `price-${symbol}`,
      async () => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 50000);
          
          const response = await fetch(
            `https://price-cryptonvx.mongo-price-storage.workers.dev/v1/price?symbol=${encodeURIComponent(symbol)}`,
            { 
              signal: controller.signal,
              headers: {
                'Accept': 'application/json'
              }
            }
          );
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            console.warn(`Erreur lors de la récupération du prix pour ${symbol}: ${response.status}`);
            return null;
          }
          
          const data = await response.json();
          if (!data || typeof data.price !== 'number') {
            console.warn(`Format de prix invalide pour ${symbol}`);
            return null;
          }
          
          return data.price;
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.warn(`Timeout lors de la récupération du prix pour ${symbol}`);
          } else {
            console.error(`Erreur lors de la récupération du prix pour ${symbol}:`, error);
          }
          return null;
        }
      },
      CACHE_DURATIONS.PRICE
    );
    
    if (price === null) return null;
    
    if (DEVISE === "EUR") {
      const priceEUR = await convertUSDEUR(price);
      return parseFloat(priceEUR.toFixed(2));
    } else {
      return parseFloat(price.toFixed(2));
    }
  } catch (error) {
    console.error(`Erreur lors du traitement du prix pour ${symbol}:`, error);
    return null;
  }
}

/**
 * Récupère les prix de plusieurs cryptomonnaies en parallèle
 */
async function getPricesForSymbols(symbols: string[]): Promise<(number | null)[]> {
  if (!symbols || !Array.isArray(symbols) || symbols.length === 0) {
    return [];
  }
  
  const prices = await Promise.all(
    symbols.map(symbol => getPriceForSymbol(symbol))
  );
  
  return prices;
}

/**
 * Interface pour les données de portefeuille de l'API
 */
interface PortfolioApiData {
  pairs: string[];
  quantity: (string | number)[];
  bid_price: (string | number)[];
  stacked_income?: number;
  airdrop_income?: number;
}

/**
 * Interface pour les données de crypto formatées
 */
interface CryptoData {
  id: string;
  symbol: string;
  name: string;
  amount: number;
  price_buy: number;
  price_current: number;
  value_current: number;
  value_initial: number;
  gain_perte: number;
  gain_perte_pourcentage: number;
  icon?: string;
}

/**
 * Interface pour les données de vue d'ensemble
 */
interface OverviewData {
  totalCapital: number;
  gain: number;
  gainPercentage: number;
  stackedIncome: number;
  airdropIncome: number;
}

/**
 * Service principal de gestion des cryptomonnaies
 */
class CryptoService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  private getJWTToken(): string | null {
    const tokenData = localStorage.getItem('token');
    if (!tokenData) return null;
    
    try {
      const parsed = JSON.parse(tokenData);
      return parsed.token || parsed;
    } catch {
      return tokenData;
    }
  }

  /**
   * Récupère les données brutes du portefeuille depuis l'API
   */
  async getPortfolioRawData(): Promise<PortfolioApiData | null> {
    const jwtToken = this.getJWTToken();
    if (!jwtToken) throw new Error('Token JWT manquant');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 50000);

      const response = await fetch(
        `https://api.cryptonvx.com/v1/app?jwt=${encodeURIComponent(jwtToken)}&type=data`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Session expirée');
        }
        throw new Error('Erreur lors de la récupération des données');
      }

      const portfolioData = await response.json();
      if (!portfolioData || !portfolioData[0]) {
        throw new Error('Aucune donnée portefeuille');
      }

      return portfolioData[0];
    } catch (error) {
      console.error('Erreur lors de la récupération des données du portefeuille:', error);
      throw error;
    }
  }

  /**
   * Récupère les données de vue d'ensemble du portefeuille
   */
  async getOverviewData(): Promise<OverviewData> {
    try {
      const data = await this.getPortfolioRawData();
      if (!data) throw new Error('Aucune donnée disponible');

      let totalCapital = 0;
      let capitalInitial = 0;

      if (Array.isArray(data.pairs) && Array.isArray(data.quantity) && Array.isArray(data.bid_price)) {
        const currentPrices = await getPricesForSymbols(data.pairs);

        for (let i = 0; i < data.pairs.length; i++) {
          const qty = Number(data.quantity[i].toString().replace(',', '.')) || 0;
          const price = currentPrices[i] || 0;
          const bid = Number(data.bid_price[i].toString().replace(',', '.')) || 0;
          
          if (qty > 0 && bid > 0) {
            capitalInitial += Number((qty * bid).toFixed(2));
            
            if (price > 0) {
              totalCapital += Number((qty * price).toFixed(2));
            }
          }
        }
      }

      const gain = Number((totalCapital - capitalInitial).toFixed(2));
      const gainPercentage = capitalInitial > 0 ? Number(((gain / capitalInitial) * 100).toFixed(2)) : 0;

      const stackedIncome = typeof data.stacked_income === "number" ? Number(data.stacked_income.toFixed(2)) : 0;
      const airdropIncome = typeof data.airdrop_income === "number" ? Number(data.airdrop_income.toFixed(2)) : 0;

      return {
        totalCapital,
        gain,
        gainPercentage,
        stackedIncome,
        airdropIncome
      };
    } catch (error) {
      console.error('Erreur lors du calcul de la vue d\'ensemble:', error);
      throw error;
    }
  }

  /**
   * Récupère les données formatées du portefeuille
   */
  async getPortfolioData(): Promise<{ cryptos: CryptoData[] }> {
    try {
      const data = await this.getPortfolioRawData();
      if (!data || !Array.isArray(data.pairs)) {
        return { cryptos: [] };
      }

      // Récupérer les icônes des cryptos
      const iconsData = await cacheService.getOrFetch(
        'crypto-icons',
        async () => {
          const response = await fetch('https://cdn.cryptonvx.com/icons-data/index.json');
          return response.ok ? await response.json() : {};
        },
        CACHE_DURATIONS.ICONS
      );

      // Récupérer les prix actuels
      const currentPrices = await getPricesForSymbols(data.pairs);

      const cryptos: CryptoData[] = [];

      for (let i = 0; i < data.pairs.length; i++) {
        const symbol = data.pairs[i];
        const amount = Number(data.quantity[i].toString().replace(',', '.')) || 0;
        const price_buy = Number(data.bid_price[i].toString().replace(',', '.')) || 0;
        const price_current = currentPrices[i] || 0;

        if (amount > 0 && price_buy > 0) {
          const value_initial = Number((amount * price_buy).toFixed(2));
          const value_current = price_current > 0 ? Number((amount * price_current).toFixed(2)) : 0;
          const gain_perte = Number((value_current - value_initial).toFixed(2));
          const gain_perte_pourcentage = value_initial > 0 ? Number(((gain_perte / value_initial) * 100).toFixed(2)) : 0;

          const cryptoInfo = iconsData[symbol];
          const icon = cryptoInfo?.image_path ? `https://cdn.cryptonvx.com/icons-data/${cryptoInfo.image_path}` : undefined;

          cryptos.push({
            id: `${symbol}-${i}`,
            symbol,
            name: cryptoInfo?.name || symbol,
            amount,
            price_buy,
            price_current,
            value_current,
            value_initial,
            gain_perte,
            gain_perte_pourcentage,
            icon
          });
        }
      }

      return { cryptos };
    } catch (error) {
      console.error('Erreur lors de la récupération du portefeuille:', error);
      throw error;
    }
  }

  /**
   * Ajoute une cryptomonnaie au portefeuille
   */
  async addCryptoToPortfolio(symbol: string, quantity: number, bidPrice: number): Promise<boolean> {
    try {
      const token = this.getJWTToken();
      if (!token) {
        throw new Error('Non authentifié');
      }

      // 1. Récupérer le portefeuille existant
      const portfolioResponse = await fetch('https://api.cryptonvx.com/v1/app?jwt=' + encodeURIComponent(token) + '&type=data', {
        headers: this.getAuthHeaders()
      });

      if (!portfolioResponse.ok) {
        if (portfolioResponse.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Session expirée');
        }
        throw new Error('Erreur lors de la récupération du portefeuille');
      }

      const portfolioData = await portfolioResponse.json();
      
      // Initialiser les listes
      let pairs: string[] = [];
      let quantities: number[] = [];
      let bidPrices: number[] = [];

      // Si le portefeuille existe, récupérer les données
      if (portfolioData && portfolioData[0]) {
        pairs = portfolioData[0].pairs || [];
        quantities = portfolioData[0].quantity.map((q: any) => parseFloat(q.toString().replace(',', '.')) || 0);
        bidPrices = portfolioData[0].bid_price.map((p: any) => parseFloat(p.toString().replace(',', '.')) || 0);
      }

      // 2. Ajouter la nouvelle crypto
      pairs.push(symbol.toUpperCase());
      quantities.push(quantity);
      bidPrices.push(bidPrice);

      // 3. Envoyer le portefeuille mis à jour
      const response = await fetch('https://api.cryptonvx.com/v1/app?jwt=' + encodeURIComponent(token), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pairs,
          quantity: quantities,
          bid_price: bidPrices
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Session expirée');
        }
        throw new Error('Erreur lors de l\'ajout de la cryptomonnaie');
      }

      const data = await response.json();
      if (data && data.error) {
        throw new Error(data.error);
      }

      // Invalider le cache du portefeuille
      cacheService.remove('portfolio-data');
      
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la cryptomonnaie:', error);
      throw error;
    }
  }

  /**
   * Met à jour une cryptomonnaie dans le portefeuille
   */
  async updateCryptoInPortfolio(index: number, newQuantity: number, newBidPrice: number): Promise<boolean> {
    try {
      const response = await fetch(`https://api.cryptonvx.com/api/portfolio/${index}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          newQuantity,
          newBidPrice
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Session expirée');
        }
        throw new Error('Erreur lors de la modification de la cryptomonnaie');
      }

      // Invalider le cache du portefeuille
      cacheService.remove('portfolio-data');
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la modification de la cryptomonnaie:', error);
      throw error;
    }
  }

  /**
   * Supprime une cryptomonnaie du portefeuille
   */
  async removeCryptoFromPortfolio(index: number): Promise<boolean> {
    try {
      const response = await fetch(`https://api.cryptonvx.com/api/portfolio/${index}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          throw new Error('Session expirée');
        }
        throw new Error('Erreur lors de la suppression de la cryptomonnaie');
      }

      // Invalider le cache du portefeuille
      cacheService.remove('portfolio-data');
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la cryptomonnaie:', error);
      throw error;
    }
  }

  /**
   * Récupère le prix actuel d'une cryptomonnaie
   */
  async getCurrentPrice(symbol: string): Promise<number | null> {
    return await getPriceForSymbol(symbol);
  }

  /**
   * Récupère les données des icônes de cryptomonnaies
   */
  async getCryptoIcons(): Promise<any> {
    return await cacheService.getOrFetch(
      'crypto-icons',
      async () => {
        const response = await fetch('https://cdn.cryptonvx.com/icons-data/index.json');
        return response.ok ? await response.json() : {};
      },
      CACHE_DURATIONS.ICONS
    );
  }

  /**
   * Vide le cache
   */
  clearCache(): void {
    cacheService.clear();
  }
}

// Export de l'instance unique du service
export const cryptoService = new CryptoService();
export default cryptoService;

// Interface pour la compatibilité avec les composants existants
export interface PortfolioData {
  cryptos: CryptoData[];
}

// Export des types pour utilisation dans les composants
export type { CryptoData, OverviewData };